import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';

import { Button, Modal, Input, Spinner, Select } from 'selfly-ui-react';
import { fetchUserKeys, saveUserKey } from '../../actions/userKeys';
import { fetchCabinets } from '../../actions/cabinets';
import { postSettingsCopyRequest } from '../../actions/controlPanel';
import { fetchOrganizationGeneralSettings, updateOrganizationGeneralSettings } from '../../api';
import { showNotification } from '../toastNotification/toastNotification';
import './consumers.css';

const PAYMENT_METHOD_OPTIONS = [
    { label: 'wipay', value: 'wipay' },
    { label: 'nayax', value: 'nayax' },
    { label: 'nayax-mini', value: 'nayax-mini' },
    { label: 'Axis-ingenico', value: 'Axis-ingenico' },
    { label: 'Logos-Valina', value: 'Logos-Valina' },
    { label: 'own_webhook', value: 'own_webhook' },
    { label: 'husky_mobile', value: 'husky_mobile' },
    { label: 'mypos', value: 'mypos' },
    { label: 'nets', value: 'nets' },
    { label: 'nets2', value: 'nets2' },
    { label: 'demo', value: 'demo' },
];

const Consumers = ({
    t,
    userKeys,
    organization,
    groupId,
    cabinets,
    isAdmin,
    fetchUserKeys,
    saveUserKey,
    fetchCabinets,
    postSettingsCopyRequest,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [blacklistRulesModalOpen, setBlacklistRulesModalOpen] = useState(false);
    const [selectedConsumer, setSelectedConsumer] = useState(null);
    const [editDisplayName, setEditDisplayName] = useState('');
    const [editPaymentMethod, setEditPaymentMethod] = useState('');
    const [newUserKey, setNewUserKey] = useState('');
    const [newDisplayName, setNewDisplayName] = useState('');
    const [newPaymentMethod, setNewPaymentMethod] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [blockedFilter, setBlockedFilter] = useState('all');
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const [currentOrganization] = useState(organization);

    const [blacklistEnabled, setBlacklistEnabled] = useState(false);
    const [repeatTimeForBlacklist, setRepeatTimeForBlacklist] = useState('');
    const [dueAmountForBlacklist, setDueAmountForBlacklist] = useState('');
    const [repeatTimeIncludingNopurchaseForBlacklist, setRepeatTimeIncludingNopurchaseForBlacklist] = useState('');
    const [applyToAllCabinets, setApplyToAllCabinets] = useState(false);
    const [loadingOrgSettings, setLoadingOrgSettings] = useState(false);
    const [savingBlacklistRules, setSavingBlacklistRules] = useState(false);

    useEffect(() => {
        if (!userKeys.fetched && !userKeys.isFetching) {
            fetchUserKeys();
        }
        if (!cabinets.fetched && !cabinets.isFetching) {
            fetchCabinets();
        }
    }, []);

    useEffect(() => {
        if (currentOrganization === organization) return;
        fetchUserKeys();
        fetchCabinets();
    }, [organization]);

    const loadBlacklistSettings = () => {
        setLoadingOrgSettings(true);
        fetchOrganizationGeneralSettings(groupId)
            .then(settings => {
                const bs = settings?.blacklistSettings || {};
                setBlacklistEnabled(!!bs.enableBlacklist);
                setRepeatTimeForBlacklist(bs.repeatTimeForBlacklist || 3);
                setDueAmountForBlacklist(bs.dueAmountForBlacklist || 50);
                setRepeatTimeIncludingNopurchaseForBlacklist(bs.repeatTimeIncludingNopurchaseForBlacklist || 9);
                setLoadingOrgSettings(false);
            })
            .catch(() => {
                setLoadingOrgSettings(false);
            });
    };

    const isBlocked = (consumer) => consumer.blockStatus === 'BLOCKED_BY_USER' || consumer.blockStatus === 'BLOCKED_BY_VALIDATION';

    const consumers = (userKeys.userKeys || []).filter(key => key.userKey);

    const filteredConsumers = consumers.filter(consumer => {
        if (statusFilter === 'active' && consumer.status !== 1) return false;
        if (statusFilter === 'inactive' && consumer.status === 1) return false;
        if (blockedFilter === 'blocked' && !isBlocked(consumer)) return false;
        if (blockedFilter === 'unblocked' && isBlocked(consumer)) return false;
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            (consumer.displayName || '').toLowerCase().includes(query) ||
            (consumer.userKey || '').toLowerCase().includes(query) ||
            (consumer.paymentMethod || '').toLowerCase().includes(query)
        );
    });

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const sortedConsumers = [...filteredConsumers].sort((a, b) => {
        if (!sortColumn) return 0;
        const dir = sortDirection === 'asc' ? 1 : -1;
        if (sortColumn === 'status') {
            return ((a.status || 0) - (b.status || 0)) * dir;
        }
        if (sortColumn === 'blocked') {
            return ((isBlocked(a) ? 1 : 0) - (isBlocked(b) ? 1 : 0)) * dir;
        }
        const valA = (a[sortColumn] || '').toLowerCase();
        const valB = (b[sortColumn] || '').toLowerCase();
        return valA.localeCompare(valB) * dir;
    });

    const getStatusLabel = (consumer) => {
        if (consumer.status === 1) {
            return { label: t('consumers:active'), className: 'consumers-table__status-badge--active' };
        }
        return { label: t('consumers:inactive'), className: 'consumers-table__status-badge--inactive' };
    };

    const handleEdit = (consumer) => {
        setSelectedConsumer(consumer);
        setEditDisplayName(consumer.displayName || '');
        setEditPaymentMethod(consumer.paymentMethod || '');
        setEditModalOpen(true);
    };

    const handleSaveEdit = () => {
        if (!selectedConsumer) return;
        const data = {
            displayName: editDisplayName,
            paymentMethod: editPaymentMethod,
        };
        saveUserKey(data, selectedConsumer.userKey)
            .then(() => {
                fetchUserKeys();
                showNotification(t('consumers:consumers'), t('consumers:updateSuccess'), 'success');
                setEditModalOpen(false);
                setSelectedConsumer(null);
            })
            .catch(() => {
                showNotification(t('consumers:consumers'), t('consumers:updateError'), 'error');
            });
    };

    const handleCreate = () => {
        if (!newUserKey.trim()) return;
        const data = {
            userKey: newUserKey.trim(),
            displayName: newDisplayName.trim(),
            paymentMethod: newPaymentMethod.trim(),
            status: 1,
            type: 2,
        };
        saveUserKey(data)
            .then(() => {
                fetchUserKeys();
                showNotification(t('consumers:consumers'), t('consumers:createSuccess'), 'success');
                setCreateModalOpen(false);
                setNewUserKey('');
                setNewDisplayName('');
                setNewPaymentMethod('');
            })
            .catch(() => {
                showNotification(t('consumers:consumers'), t('consumers:createError'), 'error');
            });
    };

    const handleToggleBlock = (consumer) => {
        const blocked = isBlocked(consumer);
        const data = { blockStatus: blocked ? 'UNBLOCKED_BY_USER' : 'BLOCKED_BY_USER' };
        saveUserKey(data, consumer.userKey)
            .then(() => {
                fetchUserKeys();
                showNotification(
                    t('consumers:consumers'),
                    blocked ? t('consumers:unblockSuccess') : t('consumers:blockSuccess'),
                    'success'
                );
            });
    };

    const handleToggleActive = (consumer) => {
        const newStatus = consumer.status === 1 ? 2 : 1;
        saveUserKey({ status: newStatus }, consumer.userKey)
            .then(() => {
                fetchUserKeys();
                showNotification(
                    t('consumers:consumers'),
                    newStatus === 1 ? t('consumers:activateSuccess') : t('consumers:deactivateSuccess'),
                    'success'
                );
            });
    };

    const handleOpenBlacklistRules = () => {
        setBlacklistRulesModalOpen(true);
        loadBlacklistSettings();
    };

    const isPositiveInteger = (val) => {
        const num = Number(val);
        return Number.isInteger(num) && num > 0;
    };

    const isValidDueAmount = (val) => {
        const num = Number(val);
        return num > 0 && /^\d+(\.\d{1,2})?$/.test(String(val).trim());
    };

    const isBlacklistFormValid = !blacklistEnabled || (
        isPositiveInteger(repeatTimeForBlacklist) &&
        isPositiveInteger(repeatTimeIncludingNopurchaseForBlacklist) &&
        isValidDueAmount(dueAmountForBlacklist)
    );

    const handleSaveBlacklistRules = () => {
        if (!isBlacklistFormValid) return;
        setSavingBlacklistRules(true);
        const settings = {
            blacklistSettings: {
                enableBlacklist: blacklistEnabled,
                repeatTimeForBlacklist: blacklistEnabled ? (Number(repeatTimeForBlacklist) || 0) : 0,
                dueAmountForBlacklist: blacklistEnabled ? (Number(dueAmountForBlacklist) || 0) : 0,
                repeatTimeIncludingNopurchaseForBlacklist: blacklistEnabled ? (Number(repeatTimeIncludingNopurchaseForBlacklist) || 0) : 0,
            },
        };

        updateOrganizationGeneralSettings(groupId, settings)
            .then(() => {
                if (applyToAllCabinets && cabinets.cabinets.length > 0) {
                    const targetCabinets = cabinets.cabinets.map(c => ({
                        id: c.id,
                        deviceCode: c.deviceCode,
                        locationId: c.locationId,
                    }));
                    const copySettings = [{ key: 'enableBlacklist', value: String(blacklistEnabled) }];
                    postSettingsCopyRequest(targetCabinets, null, copySettings)
                        .then(response => {
                            setSavingBlacklistRules(false);
                            if (response && Array.isArray(response) && response.length > 0 && response[0].status === 'success') {
                                showNotification(t('consumers:consumers'), t('consumers:blacklistRulesSavedAllCabinets'), 'success');
                            } else {
                                showNotification(t('consumers:consumers'), t('consumers:blacklistRulesSaveError'), 'error');
                            }
                            setBlacklistRulesModalOpen(false);
                            setApplyToAllCabinets(false);
                        })
                        .catch(() => {
                            setSavingBlacklistRules(false);
                            showNotification(t('consumers:consumers'), t('consumers:blacklistRulesSaveError'), 'error');
                        });
                } else {
                    setSavingBlacklistRules(false);
                    showNotification(t('consumers:consumers'), t('consumers:blacklistRulesSaved'), 'success');
                    setBlacklistRulesModalOpen(false);
                    setApplyToAllCabinets(false);
                }
            })
            .catch(() => {
                setSavingBlacklistRules(false);
                showNotification(t('consumers:consumers'), t('consumers:blacklistRulesSaveError'), 'error');
            });
    };

    if (userKeys.isFetching) {
        return (
            <div className="consumers-page">
                <div className="consumers-empty">
                    <Spinner />
                    <p>{t('consumers:loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="consumers-page">
            <div className="consumers-page__header">
                <div className="scp-settings__breadcrumb">
                    <svg>
                        <use xlinkHref="#icon-params"></use>
                    </svg>
                    <span>{t('settings')}</span>
                    <div className="scp-settings__breadcrumb-direction">
                        <svg>
                            <use xlinkHref="#icon-arrow-down"></use>
                        </svg>
                    </div>
                    <span>{t('consumers:consumers')}</span>
                </div>
                <div className="consumers-page__header-actions">
                    {isAdmin && (
                        <Button type="secondary" onClick={handleOpenBlacklistRules}>
                            {t('consumers:blacklistRules')}
                        </Button>
                    )}
                    <Button type="primary" onClick={() => setCreateModalOpen(true)}>
                        {t('consumers:addConsumer')}
                    </Button>
                </div>
            </div>

            <div className="consumers-page__filters">
                <div className="consumers-page__search">
                    <Input
                        placeholder={t('consumers:searchPlaceholder')}
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                </div>
                <div className="consumers-page__filter">
                    <label className="consumers-page__filter-label">{t('consumers:status')}</label>
                    <Select
                        size="sm"
                        value={{ label: t(`consumers:filter_${statusFilter}`), value: statusFilter }}
                        onChange={({ value }) => setStatusFilter(value)}
                        options={[
                            { label: t('consumers:filter_all'), value: 'all' },
                            { label: t('consumers:filter_active'), value: 'active' },
                            { label: t('consumers:filter_inactive'), value: 'inactive' },
                        ]}
                        isSearchable={false}
                    />
                </div>
                <div className="consumers-page__filter">
                    <label className="consumers-page__filter-label">{t('consumers:blocked')}</label>
                    <Select
                        size="sm"
                        value={{ label: t(`consumers:filter_${blockedFilter}`), value: blockedFilter }}
                        onChange={({ value }) => setBlockedFilter(value)}
                        options={[
                            { label: t('consumers:filter_all'), value: 'all' },
                            { label: t('consumers:filter_blocked'), value: 'blocked' },
                            { label: t('consumers:filter_unblocked'), value: 'unblocked' },
                        ]}
                        isSearchable={false}
                    />
                </div>
                <div className="consumers-page__stats">
                    <span>{t('consumers:totalDisplayed')}: <strong>{filteredConsumers.length}</strong></span>
                    <span>{t('consumers:active')}: <strong>{filteredConsumers.filter(c => c.status === 1).length}</strong></span>
                    <span>{t('consumers:blocked')}: <strong>{filteredConsumers.filter(c => isBlocked(c)).length}</strong></span>
                </div>
            </div>

            {filteredConsumers.length === 0 ? (
                <div className="consumers-empty">
                    <p>{searchQuery ? t('noResults') : t('consumers:noConsumers')}</p>
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table className="consumers-table">
                        <thead>
                            <tr>
                                {[
                                    { key: 'displayName', label: t('consumers:displayName') },
                                    { key: 'userKey', label: t('consumers:userKey') },
                                    { key: 'paymentMethod', label: t('consumers:paymentMethod') },
                                    { key: 'status', label: t('consumers:status') },
                                    { key: 'blocked', label: t('consumers:blocked') },
                                ].map(col => (
                                    <th
                                        key={col.key}
                                        className="consumers-table__sortable"
                                        onClick={() => handleSort(col.key)}
                                    >
                                        {col.label}
                                        <span className="consumers-table__sort-icon">
                                            {sortColumn === col.key ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                                        </span>
                                    </th>
                                ))}
                                <th>{t('consumers:actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedConsumers.map(consumer => {
                                const statusInfo = getStatusLabel(consumer);
                                return (
                                    <tr key={consumer._id || consumer.userKey}>
                                        <td>{consumer.displayName || '-'}</td>
                                        <td>{consumer.userKey}</td>
                                        <td>{consumer.paymentMethod || '-'}</td>
                                        <td>
                                            <span className={`consumers-table__status-badge ${statusInfo.className}`}>
                                                {statusInfo.label}
                                            </span>
                                        </td>
                                        <td>
                                            {isBlocked(consumer) && (
                                                <span className="consumers-table__status-badge consumers-table__status-badge--blocked">
                                                    {t('consumers:blocked')}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="consumers-table__actions">
                                                <Button
                                                    type="primary"
                                                    onClick={() => handleEdit(consumer)}
                                                    disabled={userKeys.isSaving}
                                                >
                                                    {t('edit')}
                                                </Button>
                                                <Button
                                                    type={consumer.status === 1 ? 'attention' : 'positive'}
                                                    onClick={() => handleToggleActive(consumer)}
                                                    disabled={userKeys.isSaving}
                                                >
                                                    {consumer.status === 1 ? t('consumers:deactivate') : t('consumers:activate')}
                                                </Button>
                                                <Button
                                                    type={isBlocked(consumer) ? 'positive' : 'negative'}
                                                    onClick={() => handleToggleBlock(consumer)}
                                                    disabled={userKeys.isSaving}
                                                >
                                                    {isBlocked(consumer) ? t('consumers:unblock') : t('consumers:block')}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal
                active={editModalOpen}
                onClose={() => {
                    setEditModalOpen(false);
                    setSelectedConsumer(null);
                }}
                title={t('consumers:editConsumer')}
                actions={[
                    {
                        label: t('cancel'),
                        onClick: () => {
                            setEditModalOpen(false);
                            setSelectedConsumer(null);
                        },
                    },
                    {
                        label: t('save'),
                        onClick: handleSaveEdit,
                    },
                ]}
            >
                {selectedConsumer && (
                    <div className="consumers-modal">
                        <div className="consumers-modal__field">
                            <label>{t('consumers:userKey')}</label>
                            <div className="consumers-modal__field-value">{selectedConsumer.userKey}</div>
                        </div>
                        <div className="consumers-modal__field">
                            <label>{t('consumers:displayName')}</label>
                            <Input
                                value={editDisplayName}
                                onChange={e => setEditDisplayName(e.target.value)}
                                placeholder={t('consumers:displayNamePlaceholder')}
                            />
                        </div>
                        <div className="consumers-modal__field">
                            <label>{t('consumers:paymentMethod')}</label>
                            <Select
                                size="sm"
                                value={PAYMENT_METHOD_OPTIONS.find(o => o.value === editPaymentMethod) || null}
                                onChange={({ value }) => setEditPaymentMethod(value)}
                                options={PAYMENT_METHOD_OPTIONS}
                                placeholder={t('consumers:paymentMethodPlaceholder')}
                                isSearchable={false}
                            />
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                active={createModalOpen}
                onClose={() => {
                    setCreateModalOpen(false);
                    setNewUserKey('');
                    setNewDisplayName('');
                    setNewPaymentMethod('');
                }}
                title={t('consumers:addConsumer')}
                actions={[
                    {
                        label: t('cancel'),
                        onClick: () => {
                            setCreateModalOpen(false);
                            setNewUserKey('');
                            setNewDisplayName('');
                            setNewPaymentMethod('');
                        },
                    },
                    {
                        label: t('save'),
                        onClick: handleCreate,
                    },
                ]}
            >
                <div className="consumers-modal">
                    <div className="consumers-modal__field">
                        <label>{t('consumers:userKey')} *</label>
                        <Input
                            value={newUserKey}
                            onChange={e => setNewUserKey(e.target.value)}
                            placeholder={t('consumers:userKeyPlaceholder')}
                        />
                    </div>
                    <div className="consumers-modal__field">
                        <label>{t('consumers:displayName')}</label>
                        <Input
                            value={newDisplayName}
                            onChange={e => setNewDisplayName(e.target.value)}
                            placeholder={t('consumers:displayNamePlaceholder')}
                        />
                    </div>
                    <div className="consumers-modal__field">
                        <label>{t('consumers:paymentMethod')}</label>
                        <Select
                            size="sm"
                            value={PAYMENT_METHOD_OPTIONS.find(o => o.value === newPaymentMethod) || null}
                            onChange={({ value }) => setNewPaymentMethod(value)}
                            options={PAYMENT_METHOD_OPTIONS}
                            placeholder={t('consumers:paymentMethodPlaceholder')}
                            isSearchable={false}
                        />
                    </div>
                </div>
            </Modal>

            <Modal
                active={blacklistRulesModalOpen}
                onClose={() => {
                    setBlacklistRulesModalOpen(false);
                    setApplyToAllCabinets(false);
                }}
                title={t('consumers:blacklistRules')}
                actions={[
                    {
                        label: t('cancel'),
                        onClick: () => {
                            setBlacklistRulesModalOpen(false);
                            setApplyToAllCabinets(false);
                        },
                    },
                    {
                        label: savingBlacklistRules ? t('consumers:saving') : t('save'),
                        onClick: handleSaveBlacklistRules,
                        disabled: !isBlacklistFormValid || savingBlacklistRules,
                    },
                ]}
            >
                <div className="consumers-modal consumers-modal--scrollable">
                    {loadingOrgSettings ? (
                        <div className="consumers-empty">
                            <Spinner />
                        </div>
                    ) : (
                        <>
                            <div className="consumers-modal__field">
                                <label className="consumers-modal__checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={blacklistEnabled}
                                        onChange={e => setBlacklistEnabled(e.target.checked)}
                                    />
                                    <span>{t('consumers:enableBlacklistRules')}</span>
                                </label>
                            </div>

                            {blacklistEnabled && (
                                <>
                                    <div className="consumers-modal__section-info">
                                        {t('consumers:blacklistThresholdsInfo')}
                                    </div>

                                    <div className="consumers-modal__field">
                                        <label>{t('consumers:repeatTimeForBlacklist')}</label>
                                        <Input
                                            type="number"
                                            value={repeatTimeForBlacklist}
                                            onChange={e => setRepeatTimeForBlacklist(e.target.value)}
                                            placeholder="1"
                                            min="1"
                                            step="1"
                                        />
                                        <div className="consumers-modal__field-hint">
                                            {t('consumers:repeatTimeForBlacklistHint')}
                                        </div>
                                        {repeatTimeForBlacklist !== '' && !isPositiveInteger(repeatTimeForBlacklist) && (
                                            <div className="consumers-modal__field-error">{t('consumers:validationPositiveInteger')}</div>
                                        )}
                                    </div>

                                    <div className="consumers-modal__field">
                                        <label>{t('consumers:repeatTimeIncludingNopurchaseForBlacklist')}</label>
                                        <Input
                                            type="number"
                                            value={repeatTimeIncludingNopurchaseForBlacklist}
                                            onChange={e => setRepeatTimeIncludingNopurchaseForBlacklist(e.target.value)}
                                            placeholder="1"
                                            min="1"
                                            step="1"
                                        />
                                        <div className="consumers-modal__field-hint">
                                            {t('consumers:repeatTimeIncludingNopurchaseForBlacklistHint')}
                                        </div>
                                        {repeatTimeIncludingNopurchaseForBlacklist !== '' && !isPositiveInteger(repeatTimeIncludingNopurchaseForBlacklist) && (
                                            <div className="consumers-modal__field-error">{t('consumers:validationPositiveInteger')}</div>
                                        )}
                                    </div>

                                    <div className="consumers-modal__field">
                                        <label>{t('consumers:dueAmountForBlacklist')}</label>
                                        <Input
                                            type="number"
                                            value={dueAmountForBlacklist}
                                            onChange={e => setDueAmountForBlacklist(e.target.value)}
                                            placeholder="0.00"
                                            min="0.01"
                                            step="0.01"
                                        />
                                        <div className="consumers-modal__field-hint">
                                            {t('consumers:dueAmountForBlacklistHint')}
                                        </div>
                                        {dueAmountForBlacklist !== '' && !isValidDueAmount(dueAmountForBlacklist) && (
                                            <div className="consumers-modal__field-error">{t('consumers:validationDueAmount')}</div>
                                        )}
                                    </div>

                                </>
                            )}

                            <div className="consumers-modal__divider" />

                            <div className="consumers-modal__field">
                                <label className="consumers-modal__checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={applyToAllCabinets}
                                        onChange={e => setApplyToAllCabinets(e.target.checked)}
                                    />
                                    <span>{blacklistEnabled ? t('consumers:applyToAllCabinetsEnable') : t('consumers:applyToAllCabinetsDisable')}</span>
                                </label>
                                <div className="consumers-modal__field-hint">
                                    {t('consumers:applyToAllCabinetsHint')}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default connect(
    state => ({
        userKeys: state.userKeys,
        organization: state.user.group.Name,
        groupId: state.user.group.Id,
        cabinets: state.cabinets,
        isAdmin: state.user.isAdmin,
    }),
    {
        fetchUserKeys,
        saveUserKey,
        fetchCabinets,
        postSettingsCopyRequest,
    }
)(translate('main')(Consumers));
